import { LAYOUT_PRESETS, isFacetTag, keywordGroups, presetRects, syncLeaderOf } from "@gooner/shared";
import type { Region } from "@gooner/shared";

/**
 * The SHAPE of a view as closed-vocabulary analytics props — what the region-editor
 * funnel wants to know about what people build, inside the scrub rules (see
 * tools/checks/analytics-scrub.mts, which pins this file):
 *
 * - `layout` is a preset id only when the geometry MATCHES one; a hand-dragged canvas
 *   is "custom" no matter which preset it started from, because that drift is exactly
 *   the thing worth measuring.
 * - `filters` carries recognized facet tags ONLY, sorted so the same picks always
 *   produce the same combo string. A free-text keyword is the user's own words: it is
 *   counted (`custom_terms`) and never sent.
 * - collections travel as a COUNT — their names are user-authored.
 */
export function viewShapeProps(regions: readonly Region[]): Record<string, unknown> {
  // Geometry -> preset id. Order-insensitive: presets number their rects by reading
  // order, but a region array is in stacking order and a swap is still the same layout.
  const EPS = 0.02;
  const layout =
    LAYOUT_PRESETS.find(({ id }) => {
      const rects = presetRects(id);
      if (rects.length !== regions.length) return false;
      const left = [...regions];
      for (const p of rects) {
        const i = left.findIndex(
          (r) =>
            Math.abs(r.x - p.x) < EPS &&
            Math.abs(r.y - p.y) < EPS &&
            Math.abs(r.w - p.w) < EPS &&
            Math.abs(r.h - p.h) < EPS,
        );
        if (i < 0) return false;
        left.splice(i, 1);
      }
      return true;
    })?.id ?? "custom";

  const kinds = new Map<string, number>();
  for (const r of regions) kinds.set(r.source.kind, (kinds.get(r.source.kind) ?? 0) + 1);
  const sources = [...kinds.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, n]) => `${k}:${n}`)
    .join("|") || "none";

  const words = regions.flatMap((r) =>
    r.source.kind === "keywords" ? keywordGroups(r.source).flat() : [],
  );

  return {
    regions: regions.length,
    layout,
    sources,
    ...facetCombo(words),
    synced: regions.filter((r) => syncLeaderOf(r, regions) !== null).length,
    collections: kinds.get("playlist") ?? 0,
  };
}

/**
 * A keyword list as a closed-vocabulary combo: recognized facet tags only, sorted so
 * the same picks always produce the same string, capped — eight facet tags name a
 * taste, a fifty-tag string names nothing. Free text is the user's own words: counted
 * in `custom_terms`, never sent. Shared by the per-save shape above and the per-region
 * `region_source_set` event, so the two grains stay comparable in a breakdown.
 */
export function facetCombo(words: readonly string[]): { filters: string; custom_terms: boolean } {
  const facetTags = [...new Set(words.filter(isFacetTag))].sort();
  return {
    filters: facetTags.slice(0, 8).join("|") || "none",
    custom_terms: words.some((w) => !isFacetTag(w)),
  };
}
